import React from 'react'

import { writeFile, readFile } from '@utils/fs-helpers'

import {
  getEntity,
  getHeaderData,
  getFooterData,
} from '@api/index'

import { default as normalizeHeaderData } from '@models/Header'
import { default as normalizeFooterData } from '@models/Footer'
import { default as normalizeBreadcrumb } from '@models/Breadcrumb'
import { default as normalizeBlog } from '@models/Blog'

import { Col, Container, Row } from '@/atoms/Grid'
import { ArchiveList, ArchiveSidebar } from '@/organisms'

import Layout from '@/organisms/Layout'
import Breadcrumb from '@/molecules/Breadcrumb'
import { BlogCategoryWrapper } from '@/organisms/BlogList/styles'

export const getStaticPaths = async () => {
  const blogData = await getEntity({ entity: 'blogHome' })

  const paths = []
  const categoriesMap = new Map()
  
  if (blogData.data && blogData.data.recentPosts) {
    blogData.data.recentPosts.forEach((post) => {
      if (post.category) {
        const categoryName = typeof post.category === 'string' 
          ? post.category 
          : post.category.title || post.category.name
        
        if (categoryName && !categoriesMap.has(categoryName)) {
          const slug = categoryName.toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/[^a-z0-9\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
          
          categoriesMap.set(categoryName, { slug })
          paths.push({
            params: { category: slug },
          })
        }
      }
    })
  }

  try {
    const pagesCache = Array.from(categoriesMap.values()).map((category) => ({
      slug: category.slug,
      entity: 'blog',
    }))

    await writeFile(
      'cache/static-paths-blog-categories.json',
      JSON.stringify(pagesCache),
      'utf-8'
    )
  } catch (error) {
    console.error('Generate cache file error', error)
  }

  return {
    paths,
    fallback: false,
  }
}

export const getStaticProps = async ({ params }) => {
  const cacheFile = await readFile(
    'cache/static-paths-blog-categories.json',
    'utf-8'
  )
  const staticPaths = JSON.parse(cacheFile)

  const { slug } = staticPaths.find((path) => {
    const pathSlug = Array.isArray(path.slug) ? path.slug.join('/') : path.slug
    return pathSlug === `${params.category}`
  })

  const [header, footer, blogData] = await Promise.all([
    getHeaderData(),
    getFooterData(),
    getEntity({ entity: 'blogHome' }),
  ])

  const normalizedBlog = normalizeBlog(blogData.data)
  
  let categoryData = null
  normalizedBlog.sidebar.categories.forEach((cat) => {
    if (cat.slug === slug) {
      cat.isActive = true
      categoryData = cat
    } else {
      cat.isActive = false
    }
  })

  if (!categoryData) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      metadata: {
        metaTitle: `${categoryData.name} - Blog`,
      },
      breadcrumb: normalizeBreadcrumb([
        {
          label: 'Blog',
          href: '/blog',
        },
        {
          label: categoryData.name,
        },
      ]),
      footer: normalizeFooterData(footer),
      header: normalizeHeaderData(header),
      content: {
        sidebar: {
          ...normalizedBlog.sidebar,
        },
      },
      list: {
        entity: 'blog',
        type: 'grid',
        categories: [categoryData],
        backButton: true,
        backButtonLabel: "Torna all'archivio",
      },
    },
  }
}

const CategoryBlog = ({
  metadata,
  breadcrumb,
  header,
  footer,
  content,
  list,
}) => {
  return (
    <Layout
      metadata={metadata}
      headerData={header}
      footerData={footer}
      colorScheme='white'
    >
      <Breadcrumb items={breadcrumb} />
      <BlogCategoryWrapper>
        <Container>
          <Row>
            <Col
              xs={12}
              lg={4}
            >
              <ArchiveSidebar
                {...content.sidebar}
                entity='blog'
              />
            </Col>
            <Col
              xs={12}
              lg={8}
            >
              <ArchiveList {...list} />
            </Col>
          </Row>
        </Container>
      </BlogCategoryWrapper>
    </Layout>
  )
}

export default CategoryBlog
